import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { logError } from "@/lib/log";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { PolicySectionEditor } from "@/components/PolicySectionEditor";
import { PolicyContentView } from "@/components/PolicyContentView";
import { Loader2, PenLine, Save, X } from "lucide-react";
import { toast } from "sonner";

export default function PolicyPreviewDialog({ target, onClose, onSaved }) {
  const [doc, setDoc] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [note, setNote] = useState("");
  const [notifyOwners, setNotifyOwners] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setEditing(false); setDraft(null); setNote("");
    if (!target) { setDoc(null); return; }
    api.get(`/admin/policy-content/${target.sector}/policy`, { params: { title: target.title } }).then((r) => setDoc(r.data)).catch((e) => logError("policy-preview", e));
  }, [target]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/policy-content/${target.sector}/policy`, { title: target.title, content: draft, change_note: note, notify_owners: notifyOwners });
      toast.success(`Master updated${data.alerted ? ` · ${data.alerted} subscriber${data.alerted > 1 ? "s" : ""} alerted` : ""} — rebuild documents to publish`);
      setDoc({ ...doc, content: draft, edited_at: new Date().toISOString() }); setEditing(false); onSaved?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Save failed"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="policy-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center justify-between gap-2 pr-6">
            <span>{target?.title}</span>
            {doc && (editing ? (
              <span className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => setEditing(false)} data-testid="policy-master-cancel"><X className="w-4 h-4 mr-1" /> Cancel</Button>
                <Button size="sm" onClick={save} disabled={saving} data-testid="policy-master-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save master</>}</Button>
              </span>
            ) : (
              <Button size="sm" variant="outline" onClick={() => { setDraft(doc.content); setEditing(true); }} data-testid="policy-master-edit"><PenLine className="w-4 h-4 mr-1" /> Edit master</Button>
            ))}
          </DialogTitle>
        </DialogHeader>
        {!doc && <p className="text-sm text-muted-foreground">Loading…</p>}
        {doc && editing && (
          <div className="space-y-4">
            <div className="rounded-xl border border-border bg-muted/30 p-3 space-y-2 text-sm" data-testid="policy-master-alert-options">
              <Input placeholder="What changed? (shown to subscribers — e.g. Updated LADO timescale to 1 working day)" value={note} onChange={(e) => setNote(e.target.value)} maxLength={600} data-testid="policy-master-change-note" />
              <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={notifyOwners} onCheckedChange={(c) => setNotifyOwners(!!c)} data-testid="policy-master-notify" /> Alert subscribers who own this policy (in-app + email) so they can review and re-sign</label>
            </div>
            <PolicySectionEditor content={draft} onChange={setDraft} idPrefix="master" />
          </div>
        )}
        {doc && !editing && (
          <>
            <p className="text-xs text-muted-foreground">Review frequency: {doc.content.review_frequency} · Generated {new Date(doc.generated_at).toLocaleString("en-GB")}{doc.edited_at && ` · master edited ${new Date(doc.edited_at).toLocaleString("en-GB")}`}</p>
            <PolicyContentView content={doc.content} testId="policy-master-content" />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
